import { extractErrorMessage, isUuid } from '@joyful/utils'
import type { components } from '@joyful/openapi/types.gen'
import type { ReactElement, ReactNode } from 'react'
import { lazy, Suspense, useRef, useState } from 'react'
import type { SetOptional } from 'type-fest'
import { useHotkey } from '@tanstack/react-hotkeys'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRightIcon,
  ArrowRightLeftIcon,
  ClipboardIcon,
  PanelLeftIcon,
  SearchIcon,
  ShieldUserIcon,
  SparklesIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react'

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  commandItemScopeSeparator,
  CommandList,
  CommandLoading,
  DelayedSpinner,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Keyboard,
  VisuallyHidden,
  Redact,
  toast,
  useCopyToClipboard,
  useCopyValueToClipboard,
} from '../../../core'
import type { CommandItemProps } from '../../../core'
import {
  AccountSelectionCommand,
  AccountSelectionCommandGroupedOptions,
  AccountSelectionCommandInput,
  AccountSelectionCommandList,
} from '../navbar/AccountSelectionCommand'
import type { NavbarApp, NavbarAppDefinition } from '../navbar/Navbar'
import { getNavbarAppHref, NavbarAppSchema } from '../navbar/Navbar'
import { GlobalHotkeys } from '../../utils/hotkeys'
import { useHasUserCapability } from '../../hooks/useHasUserCapability'
import { useActiveAccount } from '../../utils/activeAccountContext'
import { useCurrentUser } from '../../utils/currentUserContext'
import { AskAiProvider, useAskAi } from '../ask-ai/AskAiContext'
import { useAppRouteNavigationItems } from '../../features/navigation/utils/appRouteRegistry'
import {
  SunGuyProvider,
  sunGuyHatUrl,
  sunGuyTransparentUrl,
  useSunGuyContext,
} from '../SunGuyContext'
import { UserDropdownMenuStateProvider } from '../UserDropdownMenuContext'
import { useApiOrgSlug } from '../../utils/orgSlug'
import { useApiQueryClient } from '../../utils/reactQuery'
import { EncounterCommandItem } from '../../features/encounters/components/EncounterCommandItem'
import { useEncounterSearch } from '../../features/encounters/hooks/useEncounterSearch'
import { LeaveFeedbackProvider } from '../../features/feedback/components/LeaveFeedbackProvider'
import { ProposeChangeProvider } from '../../features/feedback/components/ProposeChangeContext'
import { usePracticeEncounterHrefs } from '../../features/encounters/utils/appEncounterHrefs'
import { serializeEncountersFiltersQueryParams } from '../../features/encounters/hooks/useEncounterFilters'
import {
  GlobalCommandEntitySearch,
  GlobalCommandEntitySearchMenu,
} from './GlobalCommandEntitySearch'
import type { GlobalCommandEntitySearchScope } from './GlobalCommandEntitySearch'
import {
  getAppNavigationLinkTo,
  GlobalCommandContext,
} from './GlobalCommandCore'

const LazyGlobalAgentChatDialog = lazy(async () => {
  const m = await import('../ask-ai/GlobalAgentChat')
  return { default: m.GlobalAgentChatDialog }
})

import type {
  AppNavigationItem,
  AppRouteNavigationSection,
  GlobalCommandContextValue,
} from './GlobalCommandCore'
export {
  GlobalCommandContext,
  getAppNavigationLinkTo,
  useGlobalCommand,
  useRegisterGlobalCommands,
} from './GlobalCommandCore'
export type {
  AppNavigationActionItem,
  AppNavigationGroupItem,
  AppNavigationItem,
  AppNavigationLinkItem,
  AppRouteNavigationSection,
  GlobalCommandContextValue,
} from './GlobalCommandCore'

type CurrentUserAccountRead = components['schemas']['CurrentUserAccountRead']

export interface NoOpGlobalCommandProviderProps {
  children: ReactNode
}

type GlobalCommandView = 'account-switch' | 'app-switch' | 'command' | null

export interface GlobalCommandProviderProps {
  accountNavigationItems?: ReadonlyArray<AppNavigationItem>
  activeApp: NavbarApp
  children: ReactNode
  generalNavigationItems?: ReadonlyArray<AppNavigationItem>
  navigationItems?: ReadonlyArray<AppNavigationItem>
  onSelectAccount?: (account: CurrentUserAccountRead) => void
  routeNavigationSections?: ReadonlyArray<AppRouteNavigationSection>
}

export function GlobalCommandProvider({
  accountNavigationItems = [],
  activeApp,
  children,
  generalNavigationItems = [],
  navigationItems = [],
  onSelectAccount,
  routeNavigationSections,
}: GlobalCommandProviderProps) {
  return (
    <AskAiProvider>
      <SunGuyProvider>
        <GlobalCommandProviderContent
          accountNavigationItems={accountNavigationItems}
          activeApp={activeApp}
          generalNavigationItems={generalNavigationItems}
          navigationItems={navigationItems}
          onSelectAccount={onSelectAccount}
          routeNavigationSections={routeNavigationSections}
        >
          {children}
        </GlobalCommandProviderContent>
      </SunGuyProvider>
    </AskAiProvider>
  )
}

function GlobalCommandProviderContent({
  accountNavigationItems = [],
  activeApp,
  children,
  generalNavigationItems = [],
  navigationItems = [],
  onSelectAccount,
  routeNavigationSections,
}: GlobalCommandProviderProps) {
  const [view, setView] = useState<GlobalCommandView>(null)
  const [registeredNavigationItems, setRegisteredNavigationItems] = useState<
    Array<AppNavigationItem>
  >([])
  const activeAccount = useActiveAccount()
  const canAccessAgent = useHasUserCapability('agent')
  const askAi = useAskAi()
  const isDockingAskAiToSidebarRef = useRef(false)
  const askAiLayoutRef = useRef(askAi.state.layout)
  askAiLayoutRef.current = askAi.state.layout

  useHotkey(
    GlobalHotkeys.globalCommand,
    () => {
      setView('command')
    },
    { ignoreInputs: true },
  )

  useHotkey(
    GlobalHotkeys.askAiDirect,
    () => {
      if (activeAccount === null) {
        return
      }

      if (askAi.state.isOpen && view === null) {
        askAi.actions.close()
        return
      }

      setView(null)
      askAi.actions.askQuestion(null)
    },
    { enabled: canAccessAgent, ignoreInputs: true },
  )

  const unregister = (key: string) => {
    setRegisteredNavigationItems((prev) => {
      return prev.filter((item) => {
        return item.key !== key
      })
    })
  }

  const contextValue: GlobalCommandContextValue = {
    open: () => {
      setView('command')
    },
    register: (newItem: AppNavigationItem) => {
      setRegisteredNavigationItems((prev) => {
        const existingItem = prev.find((item) => {
          return item.key === newItem.key
        })
        if (existingItem) {
          return prev.map((item) => {
            return item.key === newItem.key ? newItem : item
          })
        }

        return prev.concat(newItem)
      })

      return () => {
        return unregister(newItem.key)
      }
    },
    unregister,
  }
  const dockAskAiToSidebar = () => {
    isDockingAskAiToSidebarRef.current = true
    askAi.actions.setLayout('sidebar')
  }

  return (
    <GlobalCommandContext.Context value={contextValue}>
      <UserDropdownMenuStateProvider>
        <LeaveFeedbackProvider>
          <ProposeChangeProvider>
            {children}

            <Dialog
              open={view === 'command'}
              onOpenChange={(isDialogOpen) => {
                setView(isDialogOpen ? 'command' : null)
              }}
            >
              <DialogContent
                animation="none"
                positioning="top"
                aria-describedby={undefined}
                shouldHideCloseButton
                dialogOverlayProps={{
                  variant: 'light',
                }}
              >
                <VisuallyHidden asChild>
                  <DialogTitle>{`Quick navigation & actions`}</DialogTitle>
                </VisuallyHidden>

                <GlobalCommandContent
                  accountNavigationItems={accountNavigationItems}
                  activeApp={activeApp}
                  generalNavigationItems={generalNavigationItems}
                  navigationItems={navigationItems}
                  registeredNavigationItems={registeredNavigationItems}
                  closeDialog={() => {
                    setView(null)
                  }}
                  onAskAi={(question) => {
                    setView(null)
                    askAi.actions.askQuestion(question)
                  }}
                  onSelectAccount={onSelectAccount}
                  onSetFocusedView={(focusedView) => {
                    setView(focusedView)
                  }}
                  routeNavigationSections={routeNavigationSections}
                />
              </DialogContent>
            </Dialog>

            <Dialog
              open={
                askAi.state.isOpen &&
                view === null &&
                askAi.state.layout === 'dialog'
              }
              onOpenChange={(isDialogOpen) => {
                if (!isDialogOpen) {
                  if (
                    isDockingAskAiToSidebarRef.current ||
                    askAiLayoutRef.current !== 'dialog'
                  ) {
                    return
                  }

                  askAi.actions.close()
                }
              }}
            >
              <DialogContent
                animation="none"
                positioning="top"
                aria-describedby={undefined}
                shouldHideCloseButton
                className="top-[8%] flex h-[78dvh] max-w-5xl flex-col gap-0 overflow-hidden p-0 outline-hidden"
                dialogOverlayProps={{
                  variant: 'light',
                }}
                onOpenAutoFocus={(event) => {
                  isDockingAskAiToSidebarRef.current = false
                  event.preventDefault()
                }}
              >
                <div className="flex shrink-0 items-center gap-x-2 border-b py-2 pr-2 pl-4">
                  <SparklesIcon className="size-4" />
                  <DialogTitle className="flex-1 text-sm font-medium">
                    {`Ask AI`}
                  </DialogTitle>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    tooltip={`Dock to sidebar`}
                    onPointerDown={(event) => {
                      if (event.button === 0) {
                        event.preventDefault()
                        dockAskAiToSidebar()
                      }
                    }}
                    onClick={(event) => {
                      if (event.detail === 0) {
                        dockAskAiToSidebar()
                      }
                    }}
                  >
                    <PanelLeftIcon className="size-4" />
                  </IconButton>
                </div>

                {askAi.state.isOpen ? (
                  <Suspense fallback={<DelayedSpinner height="fill" />}>
                    <LazyGlobalAgentChatDialog shouldFocusInputOnMount />
                  </Suspense>
                ) : null}
              </DialogContent>
            </Dialog>

            <Dialog
              open={view === 'account-switch'}
              onOpenChange={(isDialogOpen) => {
                setView(isDialogOpen ? 'account-switch' : 'command')
              }}
            >
              <DialogContent
                animation="none"
                positioning="top"
                aria-describedby={undefined}
                shouldHideCloseButton
                dialogOverlayProps={{
                  variant: 'light',
                }}
              >
                <VisuallyHidden asChild>
                  <DialogTitle>{`Switch account`}</DialogTitle>
                </VisuallyHidden>

                {onSelectAccount ? (
                  <GlobalAccountSwitchCommand
                    onSelectAccount={(account) => {
                      onSelectAccount(account)
                      setView(null)
                    }}
                  />
                ) : null}
              </DialogContent>
            </Dialog>

            <Dialog
              open={view === 'app-switch'}
              onOpenChange={(isDialogOpen) => {
                setView(isDialogOpen ? 'app-switch' : 'command')
              }}
            >
              <DialogContent
                animation="none"
                positioning="top"
                aria-describedby={undefined}
                shouldHideCloseButton
                dialogOverlayProps={{
                  variant: 'light',
                }}
              >
                <VisuallyHidden asChild>
                  <DialogTitle>{`Switch app`}</DialogTitle>
                </VisuallyHidden>

                <GlobalAppSwitchCommand activeApp={activeApp} />
              </DialogContent>
            </Dialog>
          </ProposeChangeProvider>
        </LeaveFeedbackProvider>
      </UserDropdownMenuStateProvider>
    </GlobalCommandContext.Context>
  )
}

export function NoOpGlobalCommandProvider({
  children,
}: NoOpGlobalCommandProviderProps) {
  const [, setRegisteredNavigationItems] = useState<Array<AppNavigationItem>>(
    [],
  )

  const unregister = (key: string) => {
    setRegisteredNavigationItems((prev) => {
      return prev.filter((item) => {
        return item.key !== key
      })
    })
  }

  const contextValue: GlobalCommandContextValue = {
    open: () => {
      return undefined
    },
    register: (newItem: AppNavigationItem) => {
      setRegisteredNavigationItems((prev) => {
        const existingItem = prev.find((item) => {
          return item.key === newItem.key
        })
        if (existingItem) {
          return prev.map((item) => {
            return item.key === newItem.key ? newItem : item
          })
        }

        return prev.concat(newItem)
      })

      return () => {
        return unregister(newItem.key)
      }
    },
    unregister,
  }

  return (
    <GlobalCommandContext.Context value={contextValue}>
      {children}
    </GlobalCommandContext.Context>
  )
}

export interface SwitchToAppCommandItemProps extends Partial<
  Omit<CommandItemProps, 'onSelect'>
> {
  activeApp: NavbarApp
  onSelectCurrent?: () => void
  option: NavbarAppDefinition
}

export function SwitchToAppCommandItem({
  activeApp,
  children,
  onSelectCurrent,
  option,
  ...commandItemProps
}: SwitchToAppCommandItemProps) {
  const isSelected = option.value === activeApp

  return (
    <CommandItem
      size="xl"
      animation="none"
      isSelected={isSelected}
      keywords={[option.value]}
      subtitle={option.description}
      value={option.title}
      startAdornment={<ArrowRightLeftIcon className="size-4 stroke-2" />}
      onSelect={() => {
        if (isSelected) {
          onSelectCurrent?.()
        } else {
          const appHref = getNavbarAppHref({ app: option.value })
          window.location.assign(appHref)
        }
      }}
      {...commandItemProps}
    >
      {children ?? (
        <p>
          {`Switch to `}
          <strong>{option.title}</strong>
        </p>
      )}
    </CommandItem>
  )
}

interface SwitchToAccountCommandItemProps {
  account: CurrentUserAccountRead
  isSelected: boolean
  onSelect: () => void
}

function SwitchToAccountCommandItem({
  account,
  isSelected,
  onSelect,
}: SwitchToAccountCommandItemProps) {
  const accountLabel = account.name ?? account.slug

  return (
    <CommandItem
      size="xl"
      animation="none"
      isSelected={isSelected}
      keywords={[account.slug]}
      value={accountLabel}
      startAdornment={<ArrowRightLeftIcon className="size-4 stroke-2" />}
      onSelect={onSelect}
    >
      <p>
        {`Switch account > `}
        <strong>
          <Redact>{accountLabel}</Redact>
        </strong>
      </p>
    </CommandItem>
  )
}

interface GlobalCommandContentProps {
  accountNavigationItems: ReadonlyArray<AppNavigationItem>
  activeApp: NavbarApp
  closeDialog: () => void
  generalNavigationItems: ReadonlyArray<AppNavigationItem>
  navigationItems: ReadonlyArray<AppNavigationItem>
  onAskAi: (question: string | null) => void
  onSelectAccount?: (account: CurrentUserAccountRead) => void
  onSetFocusedView: (view: Exclude<GlobalCommandView, null | 'command'>) => void
  registeredNavigationItems: ReadonlyArray<AppNavigationItem>
  routeNavigationSections?: ReadonlyArray<AppRouteNavigationSection>
}

function GlobalCommandContent({
  accountNavigationItems,
  activeApp,
  closeDialog,
  generalNavigationItems,
  navigationItems,
  onAskAi,
  onSelectAccount,
  onSetFocusedView,
  registeredNavigationItems,
  routeNavigationSections,
}: GlobalCommandContentProps) {
  const currentUser = useCurrentUser()
  const activeAccount = useActiveAccount()
  const canAccessAgent = useHasUserCapability('agent')
  const hasCapability = useHasUserCapability()
  const sunGuy = useSunGuyContext()
  const switchableAppOptions = useSwitchableNavbarApps(activeApp)
  const [searchValue, setSearchValue] = useState('')
  const [selectedCommandValue, setSelectedCommandValue] = useState('')
  const [entitySearchScope, setEntitySearchScope] =
    useState<GlobalCommandEntitySearchScope | null>(null)
  const finalSearchValue = searchValue.trim()
  const isSearching = finalSearchValue.length > 1
  const isRankingMatches = finalSearchValue.length > 0
  const shouldShowAskAi =
    activeAccount !== null && canAccessAgent && !isUuid(finalSearchValue)
  const shouldShowSwitchAccount =
    onSelectAccount !== undefined && currentUser.accounts.length > 1
  const commandNavigationItems = flattenNavigationItems(navigationItems)
  const commandGeneralNavigationItems = flattenNavigationItems(
    generalNavigationItems,
  )
  const commandAccountNavigationItems = flattenNavigationItems(
    accountNavigationItems,
  )
  const alreadyListedNavigationItemKeys = new Set(
    [
      ...registeredNavigationItems,
      ...commandNavigationItems,
      ...commandGeneralNavigationItems,
      ...commandAccountNavigationItems,
    ].map((item) => {
      return item.key
    }),
  )
  const routeNavigationItems = useAppRouteNavigationItems({
    activeApp,
    sections: routeNavigationSections ?? [],
  }).filter((item) => {
    return !alreadyListedNavigationItemKeys.has(item.key)
  })

  if (entitySearchScope !== null && activeAccount !== null) {
    return (
      <GlobalCommandEntitySearch
        scope={entitySearchScope}
        onGoBack={() => {
          setEntitySearchScope(null)
          setSearchValue('')
        }}
        onSelect={() => {
          closeDialog()
        }}
      />
    )
  }

  return (
    <Command
      value={selectedCommandValue}
      onValueChange={setSelectedCommandValue}
    >
      <CommandInput
        placeholder="What do you need?"
        startIcon={null}
        size="lg"
        value={searchValue}
        onValueChange={(nextSearchValue) => {
          const wasSearchEmpty = searchValue.trim().length === 0
          setSearchValue(nextSearchValue)
          if (wasSearchEmpty && nextSearchValue.trim().length > 0) {
            setSelectedCommandValue('')
          }
        }}
      />

      <CommandList className="max-h-112">
        {shouldShowAskAi && finalSearchValue.length === 0 ? (
          <AskAiCommandGroup onAskAi={onAskAi} searchValue={finalSearchValue} />
        ) : null}

        <GlobalCommandSection isGrouped={isRankingMatches}>
          {registeredNavigationItems.length > 0 ? (
            <GlobalCommandSection
              isGrouped={!isRankingMatches}
              heading="Page actions"
            >
              {registeredNavigationItems.map((item) => {
                return (
                  <GoToPageCommandItem
                    key={item.key}
                    item={item}
                    closeDialog={closeDialog}
                  />
                )
              })}
            </GlobalCommandSection>
          ) : null}

          {switchableAppOptions.length > 1 || shouldShowSwitchAccount ? (
            <GlobalCommandSection
              isGrouped={!isRankingMatches}
              heading="Switch"
            >
              {switchableAppOptions.length > 1 ? (
                <CommandItem
                  size="xl"
                  animation="none"
                  startAdornment={
                    <ArrowRightLeftIcon className="size-4 stroke-2" />
                  }
                  onSelect={() => {
                    onSetFocusedView('app-switch')
                  }}
                >
                  {`Switch app`}
                </CommandItem>
              ) : null}

              {switchableAppOptions.length > 1 && finalSearchValue.length > 0
                ? switchableAppOptions.map((option) => {
                    return (
                      <SwitchToAppCommandItem
                        key={option.value}
                        activeApp={activeApp}
                        option={option}
                        onSelectCurrent={closeDialog}
                      />
                    )
                  })
                : null}

              {shouldShowSwitchAccount ? (
                <CommandItem
                  size="xl"
                  animation="none"
                  startAdornment={
                    <ArrowRightLeftIcon className="size-4 stroke-2" />
                  }
                  onSelect={() => {
                    onSetFocusedView('account-switch')
                  }}
                >
                  {`Switch account`}
                </CommandItem>
              ) : null}

              {shouldShowSwitchAccount &&
              onSelectAccount !== undefined &&
              finalSearchValue.length > 0
                ? currentUser.accounts.map((account) => {
                    return (
                      <SwitchToAccountCommandItem
                        key={account.id}
                        account={account}
                        isSelected={account.id === activeAccount?.id}
                        onSelect={() => {
                          if (account.id !== activeAccount?.id) {
                            onSelectAccount(account)
                          }

                          closeDialog()
                        }}
                      />
                    )
                  })
                : null}
            </GlobalCommandSection>
          ) : null}

          {commandNavigationItems.length > 0 ? (
            <GlobalCommandSection
              isGrouped={!isRankingMatches}
              heading={activeAccount?.name ?? `Navigation`}
            >
              {commandNavigationItems.map((item) => {
                return (
                  <GoToPageCommandItem
                    key={item.key}
                    item={item}
                    closeDialog={closeDialog}
                  />
                )
              })}
            </GlobalCommandSection>
          ) : null}

          {routeNavigationItems.length > 0 ? (
            <GlobalCommandSection
              isGrouped={!isRankingMatches}
              heading="Navigation"
            >
              {routeNavigationItems.map((item) => {
                return (
                  <GoToPageCommandItem
                    key={item.key}
                    item={item}
                    closeDialog={closeDialog}
                  />
                )
              })}
            </GlobalCommandSection>
          ) : null}

          <GlobalCommandSection isGrouped={!isRankingMatches} heading="General">
            {commandGeneralNavigationItems.map((item) => {
              return (
                <GoToPageCommandItem
                  key={item.key}
                  item={item}
                  closeDialog={closeDialog}
                />
              )
            })}

            <CopyToClipboardCommandItem
              size="xl"
              animation="none"
              startAdornment={<UserIcon className="size-4" />}
              value={currentUser.id}
              successMessage="Copied user ID to clipboard"
              onSelect={() => {
                closeDialog()
              }}
            >
              <p>
                {`Copy `}
                <strong>{`user ID`}</strong>
              </p>
            </CopyToClipboardCommandItem>

            {currentUser.clerk_id ? (
              <CopyToClipboardCommandItem
                size="xl"
                animation="none"
                startAdornment={<ShieldUserIcon className="size-4" />}
                value={currentUser.clerk_id}
                successMessage="Copied user Clerk ID to clipboard"
                onSelect={() => {
                  closeDialog()
                }}
              >
                <p>
                  {`Copy `}
                  <strong>{`Clerk user ID`}</strong>
                </p>
              </CopyToClipboardCommandItem>
            ) : null}
          </GlobalCommandSection>

          {activeAccount !== null &&
          (commandAccountNavigationItems.length > 0 ||
            hasCapability('admin.debug')) ? (
            <GlobalCommandSection
              isGrouped={!isRankingMatches}
              heading={<Redact>{activeAccount.name}</Redact>}
            >
              {commandAccountNavigationItems.map((item) => {
                return (
                  <GoToPageCommandItem
                    key={item.key}
                    item={item}
                    closeDialog={closeDialog}
                  />
                )
              })}

              {hasCapability('admin.debug') ? (
                <>
                  <CopyAccountIdToClipboardCommandItem
                    size="xl"
                    animation="none"
                    startAdornment={<UsersIcon className="size-4" />}
                    onSelect={() => {
                      closeDialog()
                    }}
                  >
                    <p>
                      {`Copy `}
                      <strong>{`account ID`}</strong>
                    </p>
                  </CopyAccountIdToClipboardCommandItem>

                  <CopyToClipboardCommandItem
                    size="xl"
                    animation="none"
                    startAdornment={<UsersIcon className="size-4" />}
                    value={activeAccount.id}
                    successMessage={`Copied Clerk account ID to clipboard`}
                    onSelect={() => {
                      closeDialog()
                    }}
                  >
                    <p>
                      {`Copy `}
                      <strong>{`Clerk account ID`}</strong>
                    </p>
                  </CopyToClipboardCommandItem>

                  <CopyToClipboardCommandItem
                    size="xl"
                    animation="none"
                    startAdornment={<ClipboardIcon className="size-4" />}
                    value={activeAccount.slug}
                    successMessage={`Copied account slug to clipboard`}
                    onSelect={() => {
                      closeDialog()
                    }}
                  >
                    <p>
                      {`Copy `}
                      <strong>{`account slug`}</strong>
                    </p>
                  </CopyToClipboardCommandItem>
                </>
              ) : null}
            </GlobalCommandSection>
          ) : null}
        </GlobalCommandSection>

        {activeAccount !== null ? (
          <GlobalCommandEntitySearchMenu
            onSelectScope={(scope) => {
              setSearchValue('')
              setEntitySearchScope(scope)
            }}
          />
        ) : null}

        {isSearching && activeAccount !== null ? (
          <CommandSearchResults
            query={finalSearchValue}
            onSelect={() => {
              closeDialog()
            }}
          />
        ) : null}

        {shouldShowAskAi && finalSearchValue.length > 0 ? (
          <AskAiCommandGroup onAskAi={onAskAi} searchValue={finalSearchValue} />
        ) : null}

        <CommandGroup heading={`Fun`}>
          <CommandItem
            size="xl"
            animation="none"
            startAdornment={
              <SunGuyCommandIcon isEnabled={sunGuy.state.isEnabled} />
            }
            onSelect={() => {
              sunGuy.actions.toggle()
              closeDialog()
            }}
          >
            {sunGuy.state.isEnabled ? `Dismiss friend` : `Add a friend`}
          </CommandItem>
          <CommandItem
            size="xl"
            animation="none"
            startAdornment={
              <SunGuyHatCommandIcon isEnabled={sunGuy.state.isHatEnabled} />
            }
            onSelect={() => {
              sunGuy.actions.toggleHat()
              closeDialog()
            }}
          >
            {sunGuy.state.isHatEnabled ? `Remove hat` : `Add a hat`}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

interface GlobalCommandSectionProps {
  children: ReactNode
  heading?: ReactNode
  isGrouped: boolean
}

/**
 * cmdk 1.1.1 sorts items within a group by match score but never reorders
 * groups. While searching, the palette merges its sections into one group so
 * the best matches rise to the top across every section.
 */
function GlobalCommandSection({
  children,
  heading,
  isGrouped,
}: GlobalCommandSectionProps) {
  return isGrouped ? (
    <CommandGroup heading={heading}>{children}</CommandGroup>
  ) : (
    children
  )
}

function flattenNavigationItems(
  items: ReadonlyArray<AppNavigationItem>,
): Array<AppNavigationItem> {
  const flattenedItems: Array<AppNavigationItem> = []
  for (const item of items) {
    if (item.type === 'group') {
      flattenedItems.push(...item.items)
    } else {
      flattenedItems.push(item)
    }
  }

  return flattenedItems
}

interface AskAiCommandGroupProps {
  onAskAi: (question: string | null) => void
  searchValue: string
}

function AskAiCommandGroup({ onAskAi, searchValue }: AskAiCommandGroupProps) {
  return (
    <CommandGroup forceMount heading="Ask AI">
      <CommandItem
        forceMount
        size="xl"
        animation="none"
        value={`ask ai`}
        startAdornment={<SparklesIcon className="size-4" />}
        endAdornment={<Keyboard>{GlobalHotkeys.askAiDirect}</Keyboard>}
        onSelect={() => {
          onAskAi(searchValue.length > 0 ? searchValue : null)
        }}
      >
        {searchValue.length > 0 ? (
          <p>
            {`Ask AI: `}
            <strong>{searchValue}</strong>
          </p>
        ) : (
          <p>{`Ask AI a question`}</p>
        )}
      </CommandItem>
    </CommandGroup>
  )
}

interface SunGuyCommandIconProps {
  isEnabled: boolean
}

function SunGuyCommandIcon({ isEnabled }: SunGuyCommandIconProps) {
  if (isEnabled) {
    return `👋`
  }

  return <SunGuyCommandIconVideo />
}

function SunGuyCommandIconVideo() {
  return (
    <video
      src={sunGuyTransparentUrl}
      autoPlay
      loop
      muted
      playsInline
      className="size-full object-contain"
    />
  )
}

interface SunGuyHatCommandIconProps {
  isEnabled: boolean
}

function SunGuyHatCommandIcon({ isEnabled }: SunGuyHatCommandIconProps) {
  if (isEnabled) {
    return `🎩`
  }

  return (
    <img
      src={sunGuyHatUrl}
      alt=""
      draggable={false}
      className="size-full object-contain"
    />
  )
}

interface GoToPageCommandItemProps {
  closeDialog: () => void
  item: AppNavigationItem
}

function GoToPageCommandItem({ closeDialog, item }: GoToPageCommandItemProps) {
  const navigate = useNavigate()

  return (
    <CommandItem
      size="xl"
      animation="none"
      keywords={[item.description, ...(item.keywords ?? [])].filter(Boolean)}
      subtitle={item.description}
      value={`${item.title}${commandItemScopeSeparator}${item.key}`}
      startAdornment={
        item.startAdornment ?? <ArrowRightIcon className="size-4 stroke-2" />
      }
      onSelect={() => {
        closeDialog()
        if (item.path) {
          if (item.href) {
            globalThis.location.assign(item.href)
          } else {
            void navigate(getAppNavigationLinkTo(item))
          }
        } else {
          item.onSelect?.()
        }
      }}
    >
      {item.path ? (
        <p>
          {`Go to `}
          <strong>{item.title}</strong>
        </p>
      ) : (
        item.title
      )}
    </CommandItem>
  )
}

function CommandSearchResults({
  onSelect,
  query,
}: {
  onSelect: () => void
  query: string
}) {
  const navigate = useNavigate()
  const orgSlug = useApiOrgSlug()
  const {
    canOpenPracticeEncounterList,
    getPracticeEncounterHref,
    getPracticeEncounterListHref,
  } = usePracticeEncounterHrefs()
  const encounterSearch = useEncounterSearch({
    limit: 10,
    query,
  })

  if (encounterSearch.isLoading) {
    return <CommandLoading>{`Loading…`}</CommandLoading>
  }

  if (encounterSearch.isSuccess) {
    const navigableEncounters = encounterSearch.encounters.flatMap(
      (encounter) => {
        const encounterHref = getPracticeEncounterHref({
          encounterId: encounter.id,
          orgSlug,
        })
        if (encounterHref === undefined) {
          return []
        }

        return [{ encounter, encounterHref }]
      },
    )

    if (!encounterSearch.isSearchValueUuid || navigableEncounters.length > 0) {
      return (
        <CommandGroup forceMount heading="Encounters">
          {navigableEncounters.map(({ encounter, encounterHref }) => {
            return (
              <EncounterCommandItem
                key={encounter.id}
                animation="none"
                account={encounter.account}
                encounter={encounter}
                onSelect={() => {
                  onSelect()
                  globalThis.location.assign(encounterHref)
                }}
              />
            )
          })}

          {encounterSearch.isSearchValueUuid ||
          !canOpenPracticeEncounterList ? null : (
            <CommandItem
              size="xl"
              animation="none"
              startAdornment={<SearchIcon className="size-4" />}
              onSelect={() => {
                onSelect()
                const encounterListHref = getPracticeEncounterListHref({
                  orgSlug,
                })
                const encountersListHref =
                  serializeEncountersFiltersQueryParams(encounterListHref, {
                    query,
                  })
                if (encountersListHref.startsWith('http')) {
                  window.location.assign(encountersListHref)
                } else {
                  void navigate(encountersListHref)
                }
              }}
            >
              <p>
                {`View all search results for `}
                <strong>{query}</strong>
              </p>
            </CommandItem>
          )}
        </CommandGroup>
      )
    }
  }

  return null
}

interface CopyToClipboardCommandItemProps extends SetOptional<
  CommandItemProps,
  'onSelect'
> {
  /**
   * @default 'Copied to clipboard'
   */
  successMessage?: string | ReactElement

  value: string
}

function CopyToClipboardCommandItem({
  children,
  disabled,
  onSelect,
  subtitle,
  successMessage = 'Copied to clipboard',
  value,
  ...props
}: CopyToClipboardCommandItemProps) {
  const [copyValue, copyMutation] = useCopyValueToClipboard({
    onSuccess: () => {
      toast.success(successMessage)
    },
    value,
  })

  return (
    <CommandItem
      {...props}
      disabled={copyMutation.isPending || disabled}
      subtitle={
        typeof subtitle === 'string' ? (
          <pre className="text-tremor-content text-xs">{subtitle}</pre>
        ) : (
          subtitle
        )
      }
      onSelect={(selectedValue) => {
        copyValue()
        onSelect?.(selectedValue)
      }}
    >
      {children}
    </CommandItem>
  )
}

type CopyAccountIdToClipboardCommandItemProps = Omit<
  CopyToClipboardCommandItemProps,
  'value'
>

function CopyAccountIdToClipboardCommandItem({
  children,
  disabled,
  onSelect,
  ...props
}: CopyAccountIdToClipboardCommandItemProps) {
  const orgSlug = useApiOrgSlug()
  const { fetchClient } = useApiQueryClient()
  const [, copyValueMutation] = useCopyToClipboard()
  const copyMutation = useMutation({
    mutationFn: async () => {
      const accountIdResponse = await fetchClient.GET('/accounts/{org_slug}', {
        params: {
          path: {
            org_slug: orgSlug,
          },
        },
      })
      if (!accountIdResponse.data) {
        throw new Error('Failed to retrieve account ID.')
      }

      await copyValueMutation.mutateAsync(accountIdResponse.data.id)
      toast.success(
        `Copied ${accountIdResponse.data.name} account ID to clipboard`,
      )
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(error)
      toast.error(`Failed to copy account ID to clipboard: ${errorMessage}`)
    },
  })

  return (
    <CommandItem
      {...props}
      disabled={copyMutation.isPending || disabled}
      onSelect={(selectedValue) => {
        copyMutation.mutate()
        onSelect?.(selectedValue)
      }}
    >
      {children}
    </CommandItem>
  )
}

interface GlobalAccountSwitchCommandProps {
  onSelectAccount: (account: CurrentUserAccountRead) => void
}

function GlobalAccountSwitchCommand({
  onSelectAccount,
}: GlobalAccountSwitchCommandProps) {
  const currentUser = useCurrentUser()
  const activeAccount = useActiveAccount()

  return (
    <AccountSelectionCommand
      state={{
        accounts: currentUser.accounts,
        activeAccount,
        isDisabled: false,
        size: 'lg',
      }}
      actions={{
        onSelect: (account) => {
          if (account !== null) {
            const selectedAccount =
              currentUser.accounts.find((currentUserAccount) => {
                return currentUserAccount.id === account.id
              }) ?? null

            if (selectedAccount !== null) {
              onSelectAccount(selectedAccount)
            }
          }
        },
      }}
    >
      <AccountSelectionCommandInput
        size="lg"
        placeholder="Switch to account…"
      />
      <AccountSelectionCommandList className="max-h-160">
        <AccountSelectionCommandGroupedOptions />
      </AccountSelectionCommandList>
    </AccountSelectionCommand>
  )
}

interface GlobalAppSwitchCommandProps {
  activeApp: NavbarApp
}

function GlobalAppSwitchCommand({ activeApp }: GlobalAppSwitchCommandProps) {
  const switchableAppOptions = useSwitchableNavbarApps(activeApp)

  return (
    <Command>
      <CommandInput placeholder="Switch to app…" startIcon={null} size="lg" />
      <CommandList className="max-h-160">
        <CommandGroup>
          {switchableAppOptions.map((option) => {
            return (
              <SwitchToAppCommandItem
                key={option.value}
                activeApp={activeApp}
                option={option}
              >
                {option.title}
              </SwitchToAppCommandItem>
            )
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

export function useSwitchableNavbarApps(
  activeApp: NavbarApp,
): Array<NavbarAppDefinition> {
  const hasCapability = useHasUserCapability()
  return NavbarAppSchema.definitions.filter((option) => {
    return option.value === activeApp || hasCapability(option.capability)
  })
}
